import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Lock, Shield, Globe, Check, ExternalLink, Zap, CreditCard, HelpCircle,
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, lowestPrice } from '@/lib/seo';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';
import { useTranslation } from '@/lib/i18n';

export default function SSLPage() {
  const { t } = useTranslation();

  const certificates = [
    {
      id: 'gogetssl-domain',
      name: 'GoGetSSL Domain SSL',
      provider: 'GoGetSSL',
      typeKey: 'ssl.type_single',
      price: '8.00',
      priceUnit: t('ssl.perYear'),
      orderUrl: 'https://deinserverhost.de/store/cart.php?a=add&pid=427',
      popular: false,
      covers: ['www.yourdomain.com', 'yourdomain.com'],
      featureKeys: ['ssl.feat1', 'ssl.feat2', 'ssl.feat3', 'ssl.feat4'],
    },
    {
      id: 'rapidssl',
      name: 'RapidSSL Standard',
      provider: 'RapidSSL',
      typeKey: 'ssl.type_single',
      price: '12.50',
      priceUnit: t('ssl.perYear'),
      orderUrl: 'https://deinserverhost.de/store/cart.php?a=add&pid=429',
      popular: false,
      covers: ['www.yourdomain.com', 'yourdomain.com'],
      featureKeys: ['ssl.feat1', 'ssl.feat2', 'ssl.feat3', 'ssl.feat4'],
    },
    {
      id: 'gogetssl-wildcard',
      name: 'GoGetSSL Wildcard SSL',
      provider: 'GoGetSSL',
      typeKey: 'ssl.type_wildcard',
      price: '75.00',
      priceUnit: t('ssl.perYear'),
      orderUrl: 'https://deinserverhost.de/store/cart.php?a=add&pid=437',
      popular: true,
      covers: ['yourdomain.com', '*.yourdomain.com'],
      featureKeys: ['ssl.feat_unlimited', 'ssl.feat_one_cert', 'ssl.feat3', 'ssl.feat4'],
    },
  ];

  const benefits = [
    { icon: Lock,       titleKey: 'ssl.ben1_title', descKey: 'ssl.ben1_desc' },
    { icon: Globe,      titleKey: 'ssl.ben2_title', descKey: 'ssl.ben2_desc' },
    { icon: Shield,     titleKey: 'ssl.ben3_title', descKey: 'ssl.ben3_desc' },
    { icon: CreditCard, titleKey: 'ssl.ben4_title', descKey: 'ssl.ben4_desc' },
  ];

  const faqs = [
    { qKey: 'ssl.faq_q1', aKey: 'ssl.faq_a1' },
    { qKey: 'ssl.faq_q2', aKey: 'ssl.faq_a2' },
    { qKey: 'ssl.faq_q3', aKey: 'ssl.faq_a3' },
    { qKey: 'ssl.faq_q4', aKey: 'ssl.faq_a4' },
  ];

  return (
    <Layout>
      <SEOHead
        title="SSL Certificates – Single Domain & Wildcard"
        description={`SSL certificates from GoGetSSL and RapidSSL from €${lowestPrice(certificates.map((c) => c.price))}/year – single-domain and wildcard certificates for secure HTTPS on your website.`}
        canonical="/ssl"
        schema={productSchema({
          name: 'SSL Certificates',
          description: 'Single-domain and wildcard SSL certificates from GoGetSSL and RapidSSL.',
          path: '/ssl',
          offers: certificates.map((cert) => ({ name: cert.name, price: cert.price, period: 'year' })),
        })}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <Badge className="mb-4 bg-primary">{t('ssl.from_badge')}</Badge>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('ssl.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('ssl.subtitle')}
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass border border-green-500/30">
              <Lock className="w-5 h-5 text-green-500" />
              <span className="font-mono text-green-500 font-bold">https://</span>
              <span className="text-muted-foreground">yourdomain.com</span>
            </div>
          </motion.div>

          {/* Certificate Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-20">
            {certificates.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className={`relative flex flex-col rounded-2xl border p-6 ${
                  cert.popular
                    ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                    : 'border-border glass'
                }`}
                data-testid={`ssl-cert-${cert.id}`}
              >
                {cert.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-xs px-3 py-1 font-bold uppercase tracking-wider">
                      {t('ssl.badge_popular')}
                    </Badge>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">{cert.provider}</span>
                  <Badge variant="secondary" className="text-xs">{t(cert.typeKey)}</Badge>
                </div>

                <h2 className="text-lg font-black mb-4">{cert.name}</h2>

                {/* Covers */}
                <div className="mb-5 space-y-1">
                  <p className="text-xs text-muted-foreground mb-1">{t('ssl.covers')}</p>
                  {cert.covers.map((domain) => (
                    <div key={domain} className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      {domain}
                    </div>
                  ))}
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6 flex-1">
                  {cert.featureKeys.map((fk) => (
                    <li key={fk} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      {t(fk)}
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-black text-primary">€{cert.price}</span>
                  <span className="text-sm text-muted-foreground">{cert.priceUnit}</span>
                </div>

                <a href={cert.orderUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    className={`w-full font-bold ${
                      cert.popular ? 'bg-primary hover:bg-primary/90 glow-hover' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                    data-testid={`order-${cert.id}-btn`}
                  >
                    {t('ssl.orderNow')}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20"
          >
            {benefits.map((b, i) => (
              <motion.div
                key={b.titleKey}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.06 }}
                className="flex gap-4 p-5 glass rounded-xl border border-border hover:border-primary/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <b.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1">{t(b.titleKey)}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{t(b.descKey)}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">{t('ssl.faqTitle')}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {faqs.map((faq, i) => (
                <motion.div
                  key={faq.qKey}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.06 }}
                  className="glass rounded-xl border border-border p-5"
                  data-testid={`faq-${i}`}
                >
                  <h3 className="font-bold text-sm mb-2">{t(faq.qKey)}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(faq.aKey)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 glass rounded-2xl"
          >
            <div>
              <h3 className="text-xl font-bold">{t('ssl.ctaTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('ssl.ctaDesc')}</p>
            </div>
            <a
              href="https://deinserverhost.de/store/submitticket.php"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover shrink-0">
                <Zap className="w-4 h-4 mr-2" />
                {t('ssl.ctaBtn')}
              </Button>
            </a>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
